import { Button, Checkbox, Input, Modal, Upload } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { CloseOutlined, DownOutlined, SearchOutlined, UploadOutlined, UpOutlined } from '@ant-design/icons';
import { downloadCSVData } from 'src/util/Util';
import './style.css';

export function extractDomain(input: string): string {
    if (!input) return input;
    let value = input.trim();
    if (!value) return value;
    value = value.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, "");
    value = value.split(/[/?#]/)[0];
    const atIdx = value.lastIndexOf("@");
    if (atIdx !== -1) value = value.slice(atIdx + 1);
    value = value.split(":")[0];
    value = value.replace(/^www\./i, "");
    return value.toLowerCase();
}

export function isValidDomain(input: string): boolean {
    if (!input) return false;
    const value = input.trim();
    if (value.length > 253) return false;
    const domainRegex = /^(?!-)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    return domainRegex.test(value);
}

export interface DealDomainListCardProps {
    title: string
    subtitle: string
    items: string[]
    acceptAll: boolean
    collapsed: boolean
    disabled?: boolean
    onToggleCollapse: () => void
    onAcceptAllChange: (val: boolean) => void
    onAdd: (value: string) => void
    onUpload: (file: File) => void
    onRemoveItem: (index: number) => void
    onClearAll: () => void
    initialFileLabel?: string | null
}

interface RemovableItemListProps {
    entries: { item: string; idx: number }[]
    onRemove: (index: number) => void
    disabled?: boolean
    emptyText?: string
}

const BATCH_SIZE = 100
const CONTAINER_HEIGHT = 400

const RemovableItemList: React.FC<RemovableItemListProps> = ({
    entries, onRemove, disabled = false, emptyText = 'No items yet',
}) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
    const scrollRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => { setVisibleCount(BATCH_SIZE) }, [entries.length])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            setVisibleCount(prev => Math.min(prev + BATCH_SIZE, entries.length))
        }
    }

    if (entries.length === 0) {
        return (
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: '10px 12px', minHeight: 48 }}>
                <span style={{ color: '#bbb', fontSize: 13 }}>{emptyText}</span>
            </div>
        )
    }

    const visibleItems = entries.slice(0, visibleCount)

    return (
        <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="removable-item-list"
            style={{
                border: '1px solid #e8e8e8', borderRadius: 6,
                padding: '10px 12px',
                maxHeight: CONTAINER_HEIGHT,
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignContent: 'start',
            }}
        >
            {visibleItems.map(({ item, idx }) => (
                <div key={item} className="ril-chip" title={item}>
                    <button
                        type="button"
                        className="ril-remove"
                        disabled={disabled}
                        aria-label={`Remove ${item}`}
                        onClick={() => { if (!disabled) onRemove(idx) }}
                    >
                        <CloseOutlined />
                    </button>
                    <span className="ril-label">{item}</span>
                </div>
            ))}
            {visibleCount < entries.length && (
                <div style={{ flexBasis: '100%', width: '100%', textAlign: 'center', color: '#888', fontSize: 12, padding: '4px 0' }}>
                    Showing {visibleCount} of {entries.length} — scroll to load more
                </div>
            )}
        </div>
    )
}

const DealDomainListCard: React.FC<DealDomainListCardProps> = ({
    title, subtitle, items, acceptAll,
    collapsed, disabled, onToggleCollapse,
    onAcceptAllChange,
    onAdd, onUpload, onRemoveItem, onClearAll,
    initialFileLabel,
}) => {
    const [inputValue, setInputValue] = useState('')
    const [validationMsg, setValidationMsg] = useState('')
    const [search, setSearch] = useState('')
    const [fileLabel, setFileLabel] = useState<string | null>(initialFileLabel ?? null)
    const [showProcessingModal, setShowProcessingModal] = useState(false)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [parsedItems, setParsedItems] = useState<string[]>([])
    const [csvFileName, setCsvFileName] = useState('')
    const [progress, setProgress] = useState(0)
    const [skippedCount, setSkippedCount] = useState(0)

    const isDomainsCard = title === 'Web Domains'

    React.useEffect(() => {
        if (initialFileLabel !== undefined) setFileLabel(initialFileLabel ?? null)
    }, [initialFileLabel])

    React.useEffect(() => {
        if (items.length === 0) {
            setFileLabel(null)
        }
    }, [items.length])

    const filtered = items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item.toLowerCase().includes(search.toLowerCase()))

    const handleAdd = () => {
        const value = isDomainsCard ? extractDomain(inputValue) : inputValue.trim()
        if (!value) return
        if (isDomainsCard && !isValidDomain(value)) {
            setValidationMsg('Please enter a valid domain name.')
            return
        }
        if (items.some(d => d.toLowerCase() === value.toLowerCase())) {
            setValidationMsg(`"${value}" has already been added.`)
            return
        }
        setValidationMsg('')
        onAdd(value)
        setInputValue('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
    }

    const handleFileUpload = (file: File) => {
        setCsvFileName(file.name)
        setProgress(0)
        setShowProcessingModal(true)
        const reader = new FileReader()
        reader.onloadend = () => {
            let prog = 0
            const interval = setInterval(() => {
                prog += 20
                setProgress(prog)
                if (prog >= 100) {
                    clearInterval(interval)
                    const allLines = (reader.result as string)
                        .split('\n')
                        .map(l => isDomainsCard ? extractDomain(l) : l.trim())
                        .filter(Boolean)
                    const lines = isDomainsCard ? allLines.filter(isValidDomain) : allLines
                    setSkippedCount(allLines.length - lines.length)
                    setParsedItems(lines)
                    setShowProcessingModal(false)
                    setShowReviewModal(true)
                }
            }, 80)
        }
        reader.readAsText(file)
        return false
    }

    const handleConfirmImport = () => {
        const existingLower = new Set(items.map(d => d.toLowerCase()))
        const unique = parsedItems.filter(d => !existingLower.has(d.toLowerCase()))
        unique.forEach(item => onAdd(item))
        setFileLabel(isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv')
        setShowReviewModal(false)
        setParsedItems([])
    }

    const handleRemoveParsedItem = (idx: number) => {
        setParsedItems(prev => prev.filter((_, i) => i !== idx))
    }

    const handleDownloadCsv = () => {
        if (items.length === 0) return
        const blob = new Blob([items.join('\n')], { type: 'text/csv;charset=utf-8;' })
        downloadCSVData(fileLabel || (isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv'), blob)
    }

    return (
        <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, marginBottom: 16, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'rgba(0,0,0,0.85)' }}>{title}</div>
                    <div style={{ color: '#595959', fontSize: 13 }}>{subtitle}</div>
                </div>
                <button type="button" onClick={onToggleCollapse} style={{
                    background: 'none', border: '1px solid #d9d9d9', borderRadius: '50%',
                    width: 32, height: 32, cursor: 'pointer', fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(0,0,0,0.65)',
                }}>
                    {collapsed ? <DownOutlined /> : <UpOutlined />}
                </button>
            </div>

            {!collapsed && (
                <div style={{ padding: '0 20px 20px' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: 'rgba(0,0,0,0.85)' }}>
                        Add {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'center' }}>
                        <Input
                            value={inputValue}
                            onChange={e => { setInputValue(e.target.value); setValidationMsg('') }}
                            onKeyDown={handleKeyDown}
                            placeholder={isDomainsCard ? 'Enter domain' : 'Enter mobile app ID'}
                            style={{ flex: 1, minWidth: 0 }}
                            disabled={acceptAll || disabled}
                        />
                        <Button type="primary" onClick={handleAdd} disabled={acceptAll || disabled} style={{ flexShrink: 0, minWidth: 72 }}>Add</Button>
                        <Upload accept=".csv,.txt" beforeUpload={handleFileUpload} showUploadList={false} disabled={acceptAll || disabled}>
                            <Button icon={<UploadOutlined />} disabled={acceptAll || disabled} style={{ flexShrink: 0 }}>Upload</Button>
                        </Upload>
                    </div>
                    <div style={{ color: '#ff4d4f', fontSize: 12, minHeight: 18, lineHeight: '18px', marginTop: 2 }}>{validationMsg}</div>
                    {fileLabel && (
                        <div style={{ fontSize: 12, color: '#888', textAlign: 'right', marginBottom: 4 }}>
                            <span
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={handleDownloadCsv}
                                title="Download uploaded CSV"
                            >
                                {fileLabel}
                            </span>{' '}
                            <span style={{ cursor: 'pointer', color: '#ff4d4f' }} onClick={onClearAll}>×</span>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12, marginTop: 12 }}>
                        <Checkbox checked={acceptAll} disabled={disabled} onChange={e => onAcceptAllChange(e.target.checked)}>All</Checkbox>
                    </div>

                    {acceptAll ? (
                        <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '32px 16px', textAlign: 'center' }}>
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>All Option is Enabled</div>
                            <div style={{ color: '#888', fontSize: 13 }}>
                                Uncheck 'All' to access {isDomainsCard ? 'domain' : 'mobile app ID'} List.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                <span style={{ fontWeight: 500, color: 'rgba(0,0,0,0.85)' }}>
                                    {isDomainsCard ? 'Domains' : 'Mobile App IDs'} List{' '}
                                    <span style={{ background: '#f0f0f0', borderRadius: 10, padding: '0 8px', fontSize: 12 }}>{items.length}</span>
                                </span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Button size="small" disabled={disabled} onClick={onClearAll}>Clear All</Button>
                                </div>
                            </div>
                            <Input
                                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                                placeholder={`Search ${isDomainsCard ? 'Domains' : 'Mobile App IDs'}`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ marginBottom: 8 }}
                            />
                            <RemovableItemList
                                entries={filtered}
                                onRemove={onRemoveItem}
                                disabled={disabled}
                            />
                        </>
                    )}
                </div>
            )}

            <Modal open={showProcessingModal} footer={null} closable={false} centered width={360}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: 'rgba(0,0,0,0.85)', marginBottom: 12 }}>Processing {csvFileName}</div>
                    <div style={{ color: '#595959', fontSize: 14, marginBottom: 16 }}>Parsing {isDomainsCard ? 'Domains' : 'Mobile App IDs'}</div>
                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, background: '#1677ff', width: `${progress}%`, transition: 'width 0.08s linear' }} />
                    </div>
                </div>
            </Modal>

            <Modal open={showReviewModal} title="Review CSV import" footer={null} onCancel={() => { setShowReviewModal(false); setParsedItems([]) }} centered width={480}>
                <p style={{ color: '#595959', fontSize: 14, marginBottom: 12 }}>
                    {parsedItems.length} {isDomainsCard ? 'domains' : 'mobile app IDs'} found in {csvFileName}. These will be added to your {isDomainsCard ? 'domains' : 'mobile app IDs'} list.
                </p>
                {isDomainsCard && skippedCount > 0 && (
                    <p style={{ color: '#ff4d4f', fontSize: 13, marginBottom: 12 }}>
                        {skippedCount} invalid domain{skippedCount > 1 ? 's were' : ' was'} skipped.
                    </p>
                )}
                <div style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: '10px 12px', minHeight: 60, maxHeight: 400, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, alignContent: 'flex-start' }}>
                    {parsedItems.map((item, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 8px', fontSize: 13, color: 'rgba(0,0,0,0.85)' }}>
                            {item}
                            <span style={{ cursor: 'pointer', color: '#999', fontWeight: 700, marginLeft: 2 }} onClick={() => handleRemoveParsedItem(idx)}>×</span>
                        </span>
                    ))}
                    {parsedItems.length === 0 && <span style={{ color: '#bbb', fontSize: 13 }}>No items</span>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button onClick={() => { setShowReviewModal(false); setParsedItems([]) }}>Cancel</Button>
                    <Button type="primary" disabled={parsedItems.length === 0} onClick={handleConfirmImport}>
                        Add {parsedItems.length} {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </Button>
                </div>
            </Modal>
        </div>
    )
}

export default DealDomainListCard;